import React from 'react';
import { useTranslation } from 'react-i18next';
import { KeyRound, Trash2 } from 'lucide-react';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '../table';
import { Profile } from '../../types/profile';
import ProfileAvatar from '../common/ProfileAvatar';

interface ProfileTableProps {
  profiles: Profile[];
  onRowClick: (profile: Profile) => void;
  onDeleteClick?: (profile: Profile) => void;
  onGenerateLinkClick?: (profile: Profile) => void;
}

const ProfileTable: React.FC<ProfileTableProps> = ({
  profiles,
  onRowClick,
  onDeleteClick,
  onGenerateLinkClick,
}) => {
  const { t } = useTranslation(['admin', 'common']);

  return (
    <div className="max-w-full overflow-x-auto">
      <Table className="w-full">
        <TableHeader className="bg-gray-50 dark:bg-white/[0.05]">
          <TableRow>
            {['user', 'city', 'role', 'status'].map(field => (
              <TableCell
                key={field}
                isHeader
                className="text-start px-6 py-4 font-semibold uppercase text-gray-600 text-[0.75rem] tracking-wide dark:text-gray-400"
              >
                {t(`table_fields.${field}`, { ns: 'admin' })}
              </TableCell>
            ))}
            <TableCell
              isHeader
              className="text-start px-6 py-4 font-semibold uppercase text-gray-600 text-[0.75rem] tracking-wide dark:text-gray-400"
            >
              {t('table_fields.actions', { ns: 'admin' })}
            </TableCell>
          </TableRow>
        </TableHeader>

        <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
          {profiles.map(profile => (
            <TableRow
              key={profile.id}
              onClick={() => onRowClick(profile)}
              className="cursor-pointer transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-white/[0.08]"
            >
              {/* Name */}
              <TableCell className="px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 overflow-hidden rounded-full ring-1 ring-gray-200 dark:ring-white/[0.1]">
                    <ProfileAvatar
                      profile_id={profile.id}
                      first_name={profile.first_name || ''}
                      avatar_url={profile.avatar_url}
                      className="w-10 h-10"
                    />
                  </div>
                  <span className="text-gray-800 font-medium dark:text-gray-200">
                    {profile.first_name} {profile.last_name}
                  </span>
                </div>
              </TableCell>

              {/* City */}
              <TableCell className="px-6 py-4 text-gray-600 dark:text-gray-400 text-sm">
                {profile.city?.name || '-'}
              </TableCell>

              {/* Role */}
              <TableCell className="px-6 py-4 text-gray-600 dark:text-gray-400 text-sm">
                <span className="inline-block px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700 dark:bg-white/[0.08] dark:text-gray-300">
                  {t(`role.${profile.role}`, { ns: 'common' })}
                </span>
              </TableCell>

              {/* Status */}
              <TableCell className="px-6 py-4 text-gray-600 dark:text-gray-400 text-sm">
                <span className="inline-block px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700 dark:bg-white/[0.08] dark:text-gray-300">
                  {t(`user_status.${profile.status}`, { ns: 'common' })}
                </span>
              </TableCell>

              {/* Actions */}
              <TableCell className="px-6 py-4 text-sm">
                <div className="flex items-center gap-3">
                  {profile.role !== 'administrator' && onGenerateLinkClick && (
                    <button
                      type="button"
                      onClick={e => {
                        e.stopPropagation();
                        onGenerateLinkClick(profile);
                      }}
                      className="text-gray-400 hover:text-primary transition-colors"
                      title={t('generateAuthLink.buttonTooltip', {
                        ns: 'admin',
                        defaultValue: 'Generate login link',
                      })}
                    >
                      <KeyRound size={18} />
                    </button>
                  )}
                  {profile.role !== 'administrator' && onDeleteClick && (
                    <button
                      type="button"
                      onClick={e => {
                        e.stopPropagation();
                        onDeleteClick(profile);
                      }}
                      className="text-gray-400 hover:text-red-600 transition-colors"
                      title={t('deleteUser.deleteButton', {
                        ns: 'admin',
                        defaultValue: 'Delete user',
                      })}
                    >
                      <Trash2 size={18} />
                    </button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default ProfileTable;
